import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enCommon from '@/locales/en/common.json';
import enHome from '@/locales/en/home.json';
import enAbout from '@/locales/en/about.json';
import enOwensCorning from '@/locales/en/owensCorning.json';
import enBookInspection from '@/locales/en/bookInspection.json';
import enServices from '@/locales/en/services.json';
import enRoofRepairs from '@/locales/en/roofRepairs.json';
import enRoofReplacement from '@/locales/en/roofReplacement.json';
import enRoofMaintenance from '@/locales/en/roofMaintenance.json';
import enNewConstruction from '@/locales/en/newConstruction.json';
import enSkylightsVentilation from '@/locales/en/skylightsVentilation.json';
import enSeniorDiscount from '@/locales/en/seniorDiscount.json';
import enFirstResponders from '@/locales/en/firstResponders.json';
import enHomeownerReferrals from '@/locales/en/homeownerReferrals.json';
import enTradePartners from '@/locales/en/tradePartners.json';
// Location page translations
import enAtascocitaRoofing from '@/locales/en/atascocitaRoofing.json';
import enHoustonRoofing from '@/locales/en/houstonRoofing.json';
import enTheWoodlandsRoofing from '@/locales/en/theWoodlandsRoofing.json';
import enKingwoodRoofing from '@/locales/en/kingwoodRoofing.json';
import enHumbleRoofing from '@/locales/en/humbleRoofing.json';
import enSpringRoofing from '@/locales/en/springRoofing.json';
import enConroeRoofing from '@/locales/en/conroeRoofing.json';
import enAldineRoofing from '@/locales/en/aldineRoofing.json';
import enWaldenRoofing from '@/locales/en/waldenRoofing.json';
import enReviews from '@/locales/en/reviews.json';
import esCommon from '@/locales/es/common.json';
import esHome from '@/locales/es/home.json';
import esAbout from '@/locales/es/about.json';
import esOwensCorning from '@/locales/es/owensCorning.json';
import esBookInspection from '@/locales/es/bookInspection.json';
import esServices from '@/locales/es/services.json';
import esRoofRepairs from '@/locales/es/roofRepairs.json';
import esRoofReplacement from '@/locales/es/roofReplacement.json';
import esRoofMaintenance from '@/locales/es/roofMaintenance.json';
import esNewConstruction from '@/locales/es/newConstruction.json';
import esSkylightsVentilation from '@/locales/es/skylightsVentilation.json';
import esSeniorDiscount from '@/locales/es/seniorDiscount.json';
import esFirstResponders from '@/locales/es/firstResponders.json';
import esHomeownerReferrals from '@/locales/es/homeownerReferrals.json';
import esTradePartners from '@/locales/es/tradePartners.json';
// Spanish location page translations
import esAtascocitaRoofing from '@/locales/es/atascocitaRoofing.json';
import esHoustonRoofing from '@/locales/es/houstonRoofing.json';
import esTheWoodlandsRoofing from '@/locales/es/theWoodlandsRoofing.json';
import esKingwoodRoofing from '@/locales/es/kingwoodRoofing.json';
import esHumbleRoofing from '@/locales/es/humbleRoofing.json';
import esSpringRoofing from '@/locales/es/springRoofing.json';
import esConroeRoofing from '@/locales/es/conroeRoofing.json';
import esAldineRoofing from '@/locales/es/aldineRoofing.json';
import esWaldenRoofing from '@/locales/es/waldenRoofing.json';
import esReviews from '@/locales/es/reviews.json';

// Supported languages
export const supportedLanguages = ['en', 'es'] as const;
export type SupportedLanguage = typeof supportedLanguages[number];

// Language display names
export const languageNames: Record<SupportedLanguage, string> = {
  en: 'English',
  es: 'Español',
};

// Check if a language is supported
export const isValidLanguage = (lang: string): lang is SupportedLanguage => {
  return supportedLanguages.includes(lang as SupportedLanguage);
};

// Get language from URL path (e.g., /es/about -> 'es')
export const getLanguageFromPath = (pathname: string): SupportedLanguage => {
  const match = pathname.match(/^\/(es)(\/|$)/);
  return match ? 'es' : 'en';
};

// Remove language prefix from path
export const removeLanguagePrefix = (pathname: string): string => {
  return pathname.replace(/^\/(es)(\/|$)/, '/');
};

// Add language prefix to path
export const addLanguagePrefix = (pathname: string, lang: SupportedLanguage): string => {
  if (lang === 'en') return pathname; // English is default, no prefix
  const cleanPath = removeLanguagePrefix(pathname);
  return `/${lang}${cleanPath === '/' ? '' : cleanPath}`;
};

i18n
  // Detect user language from browser settings
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
resources: {
      en: {
        common: enCommon,
        home: enHome,
        about: enAbout,
        owensCorning: enOwensCorning,
        bookInspection: enBookInspection,
        services: enServices,
        roofRepairs: enRoofRepairs,
        roofReplacement: enRoofReplacement,
        roofMaintenance: enRoofMaintenance,
        newConstruction: enNewConstruction,
        skylightsVentilation: enSkylightsVentilation,
        seniorDiscount: enSeniorDiscount,
        firstResponders: enFirstResponders,
        homeownerReferrals: enHomeownerReferrals,
        tradePartners: enTradePartners,
        atascocitaRoofing: enAtascocitaRoofing,
        houstonRoofing: enHoustonRoofing,
        theWoodlandsRoofing: enTheWoodlandsRoofing,
        kingwoodRoofing: enKingwoodRoofing,
        humbleRoofing: enHumbleRoofing,
        springRoofing: enSpringRoofing,
        conroeRoofing: enConroeRoofing,
        aldineRoofing: enAldineRoofing,
        waldenRoofing: enWaldenRoofing,
        reviews: enReviews,
      },
      es: {
        common: esCommon,
        home: esHome,
        about: esAbout,
        owensCorning: esOwensCorning,
        bookInspection: esBookInspection,
        services: esServices,
        roofRepairs: esRoofRepairs,
        roofReplacement: esRoofReplacement,
        roofMaintenance: esRoofMaintenance,
        newConstruction: esNewConstruction,
        skylightsVentilation: esSkylightsVentilation,
        seniorDiscount: esSeniorDiscount,
        firstResponders: esFirstResponders,
        homeownerReferrals: esHomeownerReferrals,
        tradePartners: esTradePartners,
        atascocitaRoofing: esAtascocitaRoofing,
        houstonRoofing: esHoustonRoofing,
        theWoodlandsRoofing: esTheWoodlandsRoofing,
        kingwoodRoofing: esKingwoodRoofing,
        humbleRoofing: esHumbleRoofing,
        springRoofing: esSpringRoofing,
        conroeRoofing: esConroeRoofing,
        aldineRoofing: esAldineRoofing,
        waldenRoofing: esWaldenRoofing,
        reviews: esReviews,
      },
    },
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: ['common', 'home', 'about', 'owensCorning', 'bookInspection', 'services', 'roofRepairs', 'roofReplacement', 'roofMaintenance', 'newConstruction', 'skylightsVentilation', 'seniorDiscount', 'firstResponders', 'homeownerReferrals', 'tradePartners', 'atascocitaRoofing', 'houstonRoofing', 'theWoodlandsRoofing', 'kingwoodRoofing', 'humbleRoofing', 'springRoofing', 'conroeRoofing', 'aldineRoofing', 'waldenRoofing', 'reviews'],

    // Language detection options
    detection: {
      // Order of detection methods
      order: ['path', 'localStorage', 'navigator'],
      // Cache user language preference
      caches: ['localStorage'],
      // Look for language in path
      lookupFromPathIndex: 0,
    },

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    react: {
      useSuspense: false, // We'll handle loading states manually
    },
  });

export default i18n;
